import React, { useState, useEffect, useCallback } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import supabase from '../../supabase/supabase';

const { FiUsers, FiMusic, FiDollarSign, FiShield, FiLoader, FiTrendingUp, FiActivity, FiServer, FiAlertTriangle, FiCheckSquare } = FiIcons;

export default function PlatformAdminOverview() {
  const [stats, setStats] = useState({ users: 0, revenue: 0, drops: 0, pendingApps: 0 });
  const [trendingArtists, setTrendingArtists] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, dropsRes, creatorAppsRes, adminAppsRes, revenueRes, trendingRes] = await Promise.all([
        supabase.from('d2f_profiles').select('*', { count: 'exact', head: true }),
        supabase.from('d2f_drops').select('*', { count: 'exact', head: true }),
        supabase.from('d2f_admin_access_requests').select('*', { count: 'exact', head: true }).eq('status', 'pending'),
        supabase.from('d2f_platform_admin_requests').select('*', { count: 'exact', head: true }).eq('status', 'pending'),
        supabase.from('d2f_transactions').select('amount').eq('status', 'completed'),
        supabase.from('d2f_drops').select('*, d2f_artists(name)').order('unlock_count', { ascending: false }).limit(5)
      ]);

      let totalRev = 0;
      if (revenueRes.data) {
        totalRev = revenueRes.data.reduce((sum, tx) => sum + Number(tx.amount || 0), 0);
      }

      setStats({
        users: usersRes.count || 0,
        drops: dropsRes.count || 0,
        pendingApps: (creatorAppsRes.count || 0) + (adminAppsRes.count || 0),
        revenue: totalRev
      });
      
      setTrendingArtists(trendingRes.data || []);
    } catch (err) {
      console.error('[Overview] Error fetching stats:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12 text-neutral-400">
        <SafeIcon icon={FiLoader} className="animate-spin text-2xl mr-3" />
        Loading comprehensive metrics...
      </div>
    );
  }

  const statCards = [
    { label: 'Total Users Growth', value: stats.users.toLocaleString(), trend: '+12% this week', icon: FiUsers, color: 'text-blue-400', bg: 'bg-blue-400/10' },
    { label: 'Platform Revenue', value: `$${stats.revenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, trend: '+8% this month', icon: FiDollarSign, color: 'text-green-400', bg: 'bg-green-400/10' },
    { label: 'Active Drops', value: stats.drops.toLocaleString(), trend: '+24 new', icon: FiMusic, color: 'text-purple-400', bg: 'bg-purple-400/10' },
    { label: 'Pending Applications', value: stats.pendingApps.toLocaleString(), trend: 'Action required', icon: FiShield, color: 'text-amber-400', bg: 'bg-amber-400/10' },
  ];

  return (
    <div className="space-y-6">
      {/* KPI Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((card, idx) => (
          <div key={idx} className="bg-neutral-900 border border-neutral-800 rounded-xl p-6 flex flex-col relative overflow-hidden group">
            <div className="flex items-center justify-between mb-4 relative z-10">
              <div className={`p-3 rounded-lg ${card.bg}`}>
                <SafeIcon icon={card.icon} className={`text-xl ${card.color}`} />
              </div>
              <span className="text-xs font-medium text-neutral-500 bg-black px-2 py-1 rounded-full">{card.trend}</span>
            </div>
            <h3 className="text-neutral-400 text-sm font-medium relative z-10">{card.label}</h3>
            <p className="text-2xl font-bold text-white mt-1 relative z-10">{card.value}</p>
            <div className={`absolute -bottom-6 -right-6 w-24 h-24 rounded-full ${card.bg} opacity-0 group-hover:opacity-50 transition-opacity duration-300 blur-2xl`}></div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Trending Artists / Drops */}
        <div className="lg:col-span-2 bg-neutral-900 border border-neutral-800 rounded-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold flex items-center gap-2"><SafeIcon icon={FiTrendingUp} className="text-indigo-400" /> Trending Drops</h3>
          </div>
          <div className="space-y-4">
            {trendingArtists.map((item, i) => (
              <div key={item.id} className="flex items-center justify-between p-4 bg-black/40 rounded-xl border border-neutral-800/50 hover:border-neutral-700 transition-colors">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-neutral-800 flex items-center justify-center font-bold text-neutral-500">
                    {i + 1}
                  </div>
                  <div>
                    <h4 className="font-medium text-white">{item.title}</h4>
                    <p className="text-sm text-neutral-400">{item.d2f_artists?.name || 'Unknown Artist'}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold text-indigo-400">{item.unlock_count} Unlocks</p>
                  <p className="text-xs text-neutral-500">${item.min_price || 0} min price</p>
                </div>
              </div>
            ))}
            {trendingArtists.length === 0 && <p className="text-neutral-500">No trending data available yet.</p>}
          </div>
        </div>

        {/* System Health */}
        <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold flex items-center gap-2"><SafeIcon icon={FiServer} className="text-emerald-400" /> System Health</h3>
          </div>
          <div className="space-y-6">
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-neutral-400">API Response Time</span>
                <span className="text-emerald-400 font-medium">42ms</span>
              </div>
              <div className="w-full bg-neutral-800 rounded-full h-1.5"><div className="bg-emerald-400 h-1.5 rounded-full w-[15%]"></div></div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-neutral-400">Database Load</span>
                <span className="text-amber-400 font-medium">48%</span>
              </div>
              <div className="w-full bg-neutral-800 rounded-full h-1.5"><div className="bg-amber-400 h-1.5 rounded-full w-[48%]"></div></div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-neutral-400">Storage Capacity</span>
                <span className="text-blue-400 font-medium">120 GB / 1 TB</span>
              </div>
              <div className="w-full bg-neutral-800 rounded-full h-1.5"><div className="bg-blue-400 h-1.5 rounded-full w-[12%]"></div></div>
            </div>
            
            <div className="mt-8 p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl flex items-start gap-3">
              <SafeIcon icon={FiCheckSquare} className="text-emerald-400 mt-0.5" />
              <div>
                <h4 className="text-sm font-bold text-emerald-400">All Systems Operational</h4>
                <p className="text-xs text-emerald-400/70 mt-1">Stripe, SendGrid, and Supabase services are running normally.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}